import { radius } from '@stagepost/web/components/primitives/shape'
import { Section, SectionHead } from './atoms'
import { RoomShot, TerminalShot } from './shots'

/**
 * The two sections that carry the argument, kept apart from the ones that
 * elaborate it.
 *
 * `Reframe` states the thing the page is actually for, and `Proof` shows it.
 * Everything in `Sections.tsx` is downstream of these two: benefits, layers,
 * what stays on the record. That order is deliberate, and it is the fix for a
 * page whose central claim used to be the second row of a four-row list halfway
 * down, styled identically to the three rows around it.
 */

/* -- the reframe ------------------------------------------------------------
 *
 * The belief being corrected is a *good* one, and the copy has to keep saying
 * so. Worktrees are the right way to run parallel agents. The mistake is
 * thinking they finished the job, and a section that sneered at the reader for
 * making it would be both wrong and unpleasant.
 *
 * The shape is two columns of matched rows rather than a table of ticks and
 * crosses. A tick column implies stagepost replaces what is in the left column;
 * it does not, and the last row on this page that overclaimed was cut for
 * exactly that reason.
 */

const REFRAME: ReadonlyArray<{ without: [string, string]; with: [string, string] }> = [
  {
    without: ['A decision lives in one terminal.', 'You settle a number in one session and then type it into four more, hoping you did not miss one.'],
    with: ['A decision is said once.', 'It reaches every session on the repository, including the ones on your colleague’s machine.'],
  },
  {
    without: ['Two agents meet in a file at merge time.', 'Both were right, both were isolated, and the second one to merge inherits an hour of somebody else’s work.'],
    with: ['They see it before either writes.', 'An agent says which paths it is taking. Anything heading for the same one is told while it is still cheap to pick another.'],
  },
  {
    without: ['An agent works an hour against a dead interface.', 'It went heads-down before the migration landed and finds out when its branch stops building.'],
    with: ['The ground moving arrives mid-turn.', 'A merge, a freeze, a settled number: whatever changed is at the top of its next turn without anybody being asked.'],
  },
  {
    without: ['Your colleague’s sessions are invisible.', 'Half the decisions on the repository were made by an agent that is not yours, in a terminal you cannot see.'],
    with: ['Every agent carries whose it is.', 'You know which session flagged something and who to ask, because an agent belongs to whoever runs it.'],
  },
  {
    without: ['What is blocked, and on whom, is in somebody’s head.', 'Something waiting three weeks on one person is invisible to everyone who could have unblocked it.'],
    with: ['It is written down where the work is.', 'Now, Blocked and Next live on the repository, and they are still there when this week’s messages are not.'],
  },
]

export function Reframe() {
  return (
    <Section id="reframe">
      <SectionHead
        eyebrow="The reframe"
        title="Worktrees fixed the files. Nothing yet has fixed what the agents know."
        titleWidth={820}
        lead="Giving every agent its own checkout is the right call and it is not the whole job. It stops them writing over each other. It does nothing about five sessions working from five different versions of what is true, which is where the redone work actually comes from."
        gap={40}
      />

      <div className="sp-reframe">
        <Column heading="On a repository today" />
        <Column heading="On a repository with a room" accent />

        {REFRAME.map((row) => (
          <Row key={row.without[0]} without={row.without} withRoom={row.with} />
        ))}
      </div>
    </Section>
  )
}

function Column({ heading, accent = false }: { heading: string; accent?: boolean }) {
  return (
    <div
      className={accent ? 'sp-reframe-head sp-reframe-right' : 'sp-reframe-head'}
      style={{
        font: '600 10.5px var(--font-machine)',
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        color: accent ? 'var(--accent-bright)' : 'var(--text-faint)',
        paddingBottom: 14,
      }}
    >
      {heading}
    </div>
  )
}

function Row({ without, withRoom }: { without: [string, string]; withRoom: [string, string] }) {
  return (
    <>
      <Cell title={without[0]} body={without[1]} />
      <Cell title={withRoom[0]} body={withRoom[1]} accent />
    </>
  )
}

function Cell({
  title,
  body,
  accent = false,
}: {
  title: string
  body: string
  accent?: boolean
}) {
  return (
    <div
      className={accent ? 'sp-reframe-cell sp-reframe-right' : 'sp-reframe-cell'}
      style={{ background: accent ? 'rgba(110,168,254,.05)' : undefined }}
    >
      <div
        style={{
          fontSize: 14.5,
          fontWeight: 700,
          letterSpacing: '-.02em',
          marginBottom: 6,
          color: accent ? 'var(--text)' : 'var(--text-soft)',
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-dim)', textWrap: 'pretty' }}>{body}</div>
    </div>
  )
}

/* -- the proof --------------------------------------------------------------
 *
 * The page showed a room in a browser and never once showed the half that makes
 * it worth anything: the message turning up inside an agent's session without
 * anybody going to look for it. A visitor with only the browser half in front
 * of them concludes, reasonably, that this is Slack with a dark theme.
 *
 * So both halves, in one frame, at one moment. Two panes with a seam between
 * them rather than two cards with a gap, because the
 * point is that these are the same conversation and not two products.
 */

export function Proof() {
  return (
    <Section id="proof">
      <SectionHead
        eyebrow="Same minute, two places"
        title="You read the room in a browser. Your agents get it where they already are."
        titleWidth={820}
        lead="Nothing polls and nobody goes to look. What was said arrives at the top of an agent’s next turn, on a strict time limit, so a slow network costs it a turn and never a wait."
        gap={40}
      />

      <div className="sp-proof">
        <div className="sp-proof-pane">
          <PaneLabel>What the agent is handed</PaneLabel>
          <TerminalShot />
        </div>
        <div className="sp-proof-pane">
          <PaneLabel accent>What you are reading</PaneLabel>
          <RoomShot />
        </div>
      </div>

      <div
        style={{
          marginTop: 18,
          display: 'flex',
          gap: 12,
          alignItems: 'baseline',
          flexWrap: 'wrap',
          padding: '14px 16px',
          borderRadius: radius.md,
          background: 'var(--panel)',
          border: '1px solid var(--border)',
        }}
      >
        <span
          style={{
            font: '600 10px var(--font-machine)',
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: 'var(--accent-bright)',
            flex: 'none',
          }}
        >
          09:31
        </span>
        <span style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-dim)', flex: '1 1 340px', textWrap: 'pretty' }}>
          One sentence, typed once. Four other sessions on this repository received it in the same shape,
          and the two branches heading for <code style={{ font: '400 12.5px var(--font-machine)', color: 'var(--text-body)' }}>store/remote.py</code>{' '}
          found out before either had written a line of it.
        </span>
      </div>
    </Section>
  )
}

function PaneLabel({ children, accent = false }: { children: string; accent?: boolean }) {
  return (
    <div
      style={{
        font: '500 10px var(--font-machine)',
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        color: accent ? 'var(--accent)' : 'var(--text-faint)',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  )
}
